import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, TrendingUp } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import SEOHead from "../components/SEOHead";
import PdfPageReader from "../components/PdfPageReader";
import { SubjectFeedbackSection } from "../components/FeedbackSections";
import { API_BASE_URL, BACKEND_URL } from "../lib/site";

const API = API_BASE_URL;
const BACKEND = BACKEND_URL;

export default function CaseStudyDetailPage() {
  const { id } = useParams();
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;

    setLoading(true);
    axios
      .get(`${API}/case-studies/${id}`)
      .then((res) => {
        if (!ignore) setCaseStudy(res.data);
      })
      .catch(() => {
        if (!ignore) setCaseStudy(null);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="pt-16 flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!caseStudy) {
    return (
      <div className="pt-16 flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-[#4B5563]">Case study not found.</p>
        <Link to="/case-studies" className="text-[#002FA7] font-medium flex items-center gap-1">
          <ArrowLeft size={14} /> Back to Case Studies
        </Link>
      </div>
    );
  }
  const presentationMode = caseStudy.presentation_mode || (caseStudy.pdf_url && !caseStudy.content ? "pdf" : "writeup");
  const showPdfLayout = presentationMode === "pdf" && Boolean(caseStudy.pdf_url);
  const shouldContainCover = caseStudy.cover_image?.includes("notion-zoho-product-led-cover");

  return (
    <div className="pt-16 page-enter">
      <SEOHead
        title={`${caseStudy.title} - Case Study`}
        description={caseStudy.description}
        image={
          caseStudy.cover_image
            ? caseStudy.cover_image.startsWith("/api")
              ? `${BACKEND}${caseStudy.cover_image}`
              : caseStudy.cover_image
            : undefined
        }
        url={`${BACKEND}/case-studies/${caseStudy.id}`}
      />

      <div className="border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
          <Link
            to="/case-studies"
            className="text-sm text-[#4B5563] flex items-center gap-1 hover:text-[#002FA7] transition-colors"
          >
            <ArrowLeft size={14} /> Case Studies
          </Link>
        </div>
      </div>

      {caseStudy.cover_image && (
        <div className="w-full bg-[#F9FAFB] border-b border-[#E5E7EB]">
          <div className="max-w-6xl mx-auto px-6 lg:px-12 py-5 sm:py-7">
            <div className={`overflow-hidden border border-[#E5E7EB] ${shouldContainCover ? "bg-[#8FB6B5]" : "bg-white"}`}>
              <img
                src={caseStudy.cover_image}
                alt={caseStudy.title}
                className={`w-full aspect-video ${shouldContainCover ? "object-contain" : "object-cover"} object-center`}
              />
            </div>
          </div>
        </div>
      )}

      <section className="max-w-7xl mx-auto px-6 lg:px-0 py-10 sm:py-12">
        <div className="max-w-[58rem] mx-auto">
          <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
            {caseStudy.industry}
          </p>
          <h1 className="font-['Outfit'] text-3xl sm:text-5xl font-semibold text-[#0A0A0A] leading-tight mb-5">
            {caseStudy.title}
          </h1>
          <p className="text-lg text-[#4B5563] leading-relaxed mb-10">{caseStudy.description}</p>

          {!!caseStudy.metrics?.length && (
            <div className="mb-10">
              <p className="text-[11px] tracking-[0.15em] uppercase font-bold text-[#0A0A0A] mb-4 flex items-center gap-2">
                <TrendingUp size={14} className="text-[#002FA7]" /> Key Outcomes
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {caseStudy.metrics.map((metric, index) => (
                  <div key={`${metric.label}-${index}`} className="border border-[#E5E7EB] bg-white p-5">
                    <p className="font-['Outfit'] text-3xl font-bold text-[#002FA7]">{metric.value}</p>
                    <p className="text-sm text-[#6B7280] mt-1">{metric.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {showPdfLayout ? (
          <PdfPageReader src={`${API}/case-studies/${caseStudy.id}/pdf`} title={caseStudy.title} />
        ) : caseStudy.content ? (
          <div className="prose-article case-study-prose" data-testid="case-study-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {caseStudy.content}
            </ReactMarkdown>
          </div>
        ) : caseStudy.pdf_url ? (
          <div className="border border-[#E5E7EB] bg-white p-5">
            <div className="mb-4">
              <p className="text-[11px] tracking-[0.15em] uppercase font-bold text-[#0A0A0A] mb-1">
                Full Case Study
              </p>
              <p className="text-sm text-[#6B7280]">
                Embedded below for reading on the page.
              </p>
            </div>
            <iframe
              src={`${API}/case-studies/${caseStudy.id}/pdf#view=FitH`}
              className="w-full h-[72vh] sm:h-[90vh] border border-[#E5E7EB]"
              title={caseStudy.title}
            />
          </div>
        ) : (
          <div className="border border-dashed border-[#D1D5DB] bg-[#F9FAFB] p-8 text-center">
            <p className="text-sm text-[#6B7280] mb-3">
              The full case study file is not uploaded yet.
            </p>
            <Link to="/book" className="text-sm font-semibold text-[#002FA7] hover:underline">
              Book a call to discuss this work
            </Link>
          </div>
        )}
      </section>

      <SubjectFeedbackSection
        subjectType="case_study"
        subjectId={caseStudy.id}
        placeholder="What felt sharp, credible, surprising, or worth debating?"
      />
    </div>
  );
}
